import { Component, OnInit, OnDestroy, Input, OnChanges, SimpleChanges, ElementRef, ChangeDetectorRef } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { fuseAnimations } from '@fuse/animations';
import { CoursesAPIService } from '@shared/services/coursesApi.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { PaginatedScrollService, PaginatedScrollController } from '@shared/services/paginated-scroll.service';
import { Course, Learnspace, UserType } from '@shared/models';
import { ProgramReportingSessionLogComponent } from './program-reporting-session-log/program-reporting-session-log.component';
import { AssessmentReportDialogComponent } from '@app/main/analytics/assessment/assessment-report-dialog/assessment-report-dialog.component';
const defaultSection = 'reports';

@Component({
  selector: 'program-reporting',
  templateUrl: './program-reporting.component.html',
  styleUrls: ['./program-reporting.component.scss'],
  animations: fuseAnimations
})
export class ProgramReportingComponent implements OnInit, OnDestroy, OnChanges {
  @Input() learnspace: Learnspace;
  courses: Course[] = [];
  activeCourseId: string = '';
  activeCourse: Course = null;
  activeMode: string = defaultSection;
  selectedUserType = UserType.all;

  private _paginationController: PaginatedScrollController<Course>;
  private subscription: Subscription;

  constructor(
    private _courseApiService: CoursesAPIService,
    private _router: Router,
    private _route: ActivatedRoute,
    private _dialog: MatDialog,
    private _elRef: ElementRef,
    private _cdr: ChangeDetectorRef,
    private _paginatedScrollService: PaginatedScrollService
  ) {}

  async ngOnInit() {
    this.subscription = this._route.queryParams.subscribe(async (params) => {
      await this.updateContent();
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this._paginationController?.destroy();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.learnspace) {
      if (this.learnspace?.id) {
        this.loadCourses();
      } else {
        this.courses = [];
        this.activeCourse = null;
        this.activeCourseId = '';
      }
    }
  }

  private loadCourses(): void {
    this._paginationController?.destroy();
    this._paginationController = this._paginatedScrollService.createController(
      options => this._courseApiService.getLearnspaceCourseList(this.learnspace.id, options)
        .pipe(map(({ total, courses }) => ({ total, items: courses }))),
      items => this.courses = items,
      { pageSize: 30, sort: 'position' }
    );
    this._paginationController.reload().subscribe({
      next: () => {
        this._cdr.detectChanges();
        this._paginationController.attachScroll(this.getScrollContainer());
        if (!this.activeCourseId && this.courses.length > 0) {
          this.activeCourse = this.courses[0];
          this.activeCourseId = this.activeCourse.id;
        }
      }
    });
  }

  private getScrollContainer(): Element {
    const inner = this._elRef.nativeElement.querySelector('.mat-drawer-inner-container');
    return inner || document.documentElement;
  }

  private async updateContent() {
    this.activeMode = this._route.snapshot.queryParams.section || defaultSection;
    this.activeCourseId = this._route.snapshot.queryParams.simulationId;
    if (this.activeCourseId && this.activeCourseId != this.activeCourse?.id) {
      this.activeCourse = undefined; // This is needed to ensure that the old course is not displayed while waiting for the new one.
      this.activeCourse = await this._courseApiService.getCourseById(this.activeCourseId).toPromise();
    }
  }

  goToCourse(simulationId: string, section: string = defaultSection) {
    this._router.navigate([], {
      relativeTo: this._route,
      queryParams: { simulationId, section },
      queryParamsHandling: 'merge'
    });
  }

  onOpenSessionLog(event: any) {
    const dialog = this._dialog.open(ProgramReportingSessionLogComponent, {
      width: 'min(80vw, 1200px)',
      maxHeight: 'min(85vh, 800px)',
      panelClass: 'learnspace-theme',
      data: { sessionId: event.sessionId, course: event.course },
      autoFocus: false
    });
    dialog.componentInstance.onExplore.subscribe(event => this.openAssessmentDashboard(event));
  }

  openAssessmentDashboard(event: any) {
    const dialog = this._dialog.open(AssessmentReportDialogComponent, {
      width: 'min(80vw, 1200px)',
      height: 'min(85vh, 800px)',
      panelClass: ['learnspace-theme', 'assessment-report-dialog'],
      data: { assessment: event.assessmentResult, course: event.course },
      autoFocus: false
    });
    dialog.componentInstance.onExplore.subscribe(event => this.onOpenSessionLog(event));
  }

  handleUserTypeChange(event: UserType) {
    this.selectedUserType = event;
  }
}
